<template>
	<div class="business-switcher-container">
		<ModalHeader
			:title="getString('select_business')"
			@click-close="$emit('done')" >

			<template #extra-buttons>
				<div class="search-container">
					<AppTextInput 
					ref="search-input" 
					icon-name="icon-magnify" 
					:placeholder="getString('search')" 
					v-model="searchQuery"
					@changed-text="handleSearchChange" />
				</div>
			</template>

		</ModalHeader>
		
		<div class="business-list-wrapper">
			<AppList v-if="businessItems.length > 0" :items="businessItems">
				<template #business-item="{item}">
					<div 
						class="business-item-content"
						:class="{'is-current': item.business_id === currentBusinessId}"
						@click="item.business_id !== currentBusinessId && switchToBusiness(item.business_id)">
						<div class="business-logo">
							<img v-if="item.logo" :src="item.logo" :alt="item.name">
							<div v-else class="business-logo-placeholder">
								<img src="https://img.qromo.io/img/logo.svg" alt="Default logo">
							</div>
						</div>
						
						<div class="business-info">
							<h3 class="business-name">{{ item.name }}</h3>
							<div class="permissions-container">
								<div class="permissions-list">
									<span v-for="permission in item.permissions" :key="permission" class="permission-badge">
										{{ getString(permission) }}
									</span>
								</div>
							</div>
						</div>
						
						<div class="business-action">
							<div  v-if="item.business_id !== currentBusinessId" class="select-arrow">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
									<path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</div>
						</div>

					</div>
				</template>
			</AppList>
			<div v-else class="no-results">
				<p>{{ getString('no_results_found') }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import {defineComponent} from 'vue'
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";

export default defineComponent({
	name: "BusinessSwitcher",
	components: {AppTextInput, AppList, ModalHeader},
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {
			userStore,
			getString: languageStore.getString
		}
	},
	data() {
		return {
			searchQuery: ''
		}
	},
	emits: ["done"],
	computed: {
		currentBusinessId() {
			return Number(this.userStore.currBusinessId);
		},
		businessItems() {
			if (!this.userStore.businesses) {
				return [];
			}
			
			// Convert businesses object to array for AppList
			let businesses = Object.values(this.userStore.businesses);
			
			// Filter by search query if present
			if (this.searchQuery && this.searchQuery.trim()) {
				const query = this.searchQuery.toLowerCase().trim();
				businesses = businesses.filter(business => 
					business.name.toLowerCase().includes(query)
				);
			}
			
			return businesses.map(business => {
				// Get user's permissions for this business
				const permissions = business.permissions || [];
				
				// Merge orders permissions into a single "orders_and_pay" permission
				const ordersPermissions = [
					'orders_creation',
					'orders_edit',
					'orders_pay_pos',
					'orders_pay_cash',
					'orders_pay_other',
					'orders_discounts',
					'orders_daily_closing'
				];
				
				// Check if any orders permission exists
				const hasOrdersPermission = permissions.some(p => ordersPermissions.includes(p));
				
				// Filter out individual orders permissions and add merged one if needed
				let mergedPermissions = permissions.filter(p => !ordersPermissions.includes(p));
				if (hasOrdersPermission) {
					mergedPermissions.push('orders');
				}
				
				return {
					title: business.name, // Required by AppList
					customSlot: 'business-item',
					business_id: business.business_id,
					name: business.name,
					logo: business.logo,
					permissions: mergedPermissions
				};
			});
		}
	},
	methods: {
		handleSearchChange(newValue) {
			this.searchQuery = newValue;
		},
		switchToBusiness(businessId) {
			// Change the business using the store method
			this.userStore.changeBusiness(businessId);
			// Close the switcher
			this.$emit('done');
		}
	}
})
</script>

<style lang="scss" scoped>
.business-switcher-container {
	background-color: var(--color-bg-primary-1);
	border-radius: 13px 13px 0 0;
	display: flex;
	flex-direction: column;


	.search-container {
		.input-wrapper {
			width: 180px;
		}
	}

	.business-list-wrapper {
		flex: 1;

		:deep(.list-container) {
			.list-item {
				margin-bottom: 12px;
				padding: 0;
				
				&:before, &:after {
					display: none;
				}
			}
		}
		
		.business-item-content {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px;
			width: 100%;
			transition: background-color 0.2s ease;
			border-radius: 13px;
			background-color: var(--color-alpha-3);
			border: 1px solid var(--color-primary);
			cursor: pointer;

			&.is-current {
				background-color: var(--color-alpha-1);
				cursor: auto;
				border: 1px solid transparent;
			}
			
			.business-logo {
				width: 60px;
				height: 60px;
				flex-shrink: 0;
				
				img {
					width: 100%;
					height: 100%;
					border-radius: 10px;
					object-fit: cover;
				}
				
				.business-logo-placeholder {
					width: 100%;
					height: 100%;
					background-color: var(--color-bg-primary-1);
					border-radius: 10px;
					display: flex;
					align-items: center;
					justify-content: center;
					
					img {
						width: 30px;
						height: 30px;
					}
				}
			}
			
			.business-info {
				flex: 1;
				min-width: 0;
				
				.business-name {
					font-size: 18px;
					font-weight: 600;
					color: var(--color-txt);
					margin: 0 0 8px 0;
					font-family: 'Montserrat', sans-serif;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}
				
				.permissions-container {
					display: flex;
					flex-direction: column;
					gap: 4px;
					
					.permissions-label {
						font-size: 12px;
						color: var(--color-txt-2);
						font-family: 'Montserrat', sans-serif;
					}
					
					.permissions-list {
						display: flex;
						flex-wrap: wrap;
						gap: 4px;
						
						.permission-badge {
							border: 1px solid var(--color-txt-2);
							color: var(--color-txt);
							padding: 1px 6px;
							border-radius: 6px;
							font-size: 11px;
							font-family: 'Montserrat', sans-serif;
							font-weight: 500;
						}
					}
				}
			}
			
			.business-action {
				flex-shrink: 0;
				display: flex;
				align-items: center;

				.current-badge {
					background-color: var(--color-primary);
					color: var(--color-txt-white);
					padding: 6px 12px;
					border-radius: 8px;
					font-size: 12px;
					font-family: 'Montserrat', sans-serif;
					font-weight: 600;
				}
				
				.select-arrow {
					color: var(--color-primary);
					display: flex;
					align-items: center;
					justify-content: center;
					text-align: center;
				}
			}
		}
		
		.no-results {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 40px 20px;
			
			p {
				color: var(--color-txt-2);
				font-size: 16px;
				font-family: 'Montserrat', sans-serif;
				text-align: center;
			}
		}
	}
	
	.search-container {
		width: 100%;
		max-width: 300px;
		
		:deep(.AppTextInput) {
			input {
				background-color: var(--color-alpha-1);
				border: 1px solid var(--color-alpha-2);
				
				&:focus {
					border-color: var(--color-primary);
					background-color: var(--color-bg-primary-1);
				}
			}
		}
	}

	@media (max-width: 500px) {

		.business-list-wrapper {

			.business-item-content {

				.business-logo {
					width: 45px;
					height: 45px;
				}
				.business-info {

					.business-name {
						margin-bottom: 0;
					}
					.permissions-container {
						display: none;
					}
				}
			}
		}
	}
}
</style>